import t from "prop-types";
import { Dialog, DialogTitle, DialogContent, DialogActions, IconButton, CircularProgress, Tooltip } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import Button from "./Button";
import { Trans } from "@lingui/macro";
import CommonButton from "@app/common/components/material_ui/Button";

import { css } from "@emotion/react";
import { forwardRef } from "react";
// import FullScreenLoading from "../FullScreenLoad";

const diagTitleRootStyle = ({ colors }) => css`
  min-width: 17vw;
  text-transform: uppercase;
  /* padding-right: 24px; */
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: ${colors.text.main};

  .title-div{
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    flex: 1;
  }

  .top-right-buttons {
    display: flex;
    flex-direction: row;

    .top-left-x-icon {
      color: #777;
    }
  }

  @media print {
    .top-left-x-icon,
    .MuiDialogActions-root {
      display: none;
    }
  }
`;

const actionsCSS = ({ colors }) => css`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex: 1;

  > .space { flex: 1; }

  @media print {
    display: none;
  }

  .MuiCircularProgress-colorPrimary {
    color: ${colors.primary.dark};
  }
`;

const dialogContentStyles = ({ colors }) => css`
  color: ${colors.text.main};
`;

const CommonDialog = forwardRef((parentProps, ref) => {
  const props = { ...parentProps };

  let dialogTitle = null;
  let dialogContent = null;
  let dialogActions = null;
  if (props.children) {
    const children = Array.isArray(props.children) ? props.children : [props.children];

    children.forEach(child => {
      if (!child.props || !child.props.componentname) {
        return;
      }
      switch (child.props.componentname) {
        case "DialogTitle":
          dialogTitle = child;
          break;
        case "DialogContent":
          dialogContent = child;
          break;
        case "DialogActions":
          dialogActions = child;
          break;
        default:
          break;
      }
    });
  }

  const {
    cancelAction,
    cancelLabel,
    okAction,
    okLabel,
    okColor,
    title,
    content,
    noCancelButton,
    isLoadingActions,
    okDisabled,
    okDisabledTooltipZIndex,
    okDisabledTooltip,
    noCloseOnFocusOut,
    contentLoading,
    noXicon,
    leftSideAction,
    customActions,
    xAction,
    topRightActions,
    style,
    titleContainerStyle,
    dialogActionStyle,
    dialogContentStyle,
    closeBtnStyle,
    hideActionRow = false,
    ...otherProps } = props;
  let isrtl = props.isrtl; //eslint-disable-line
  if (props.isrtl === undefined) isrtl = true;
  const styleDirection = isrtl ? "rtl" : "ltr";
  // looks like material-ui trying to pass all props directly to html objects... throws warnings if not deleted :\

  const xCancelAction = xAction || cancelAction;

  if (contentLoading) {
    return (
      <Dialog
        style={{ position: "absolute", ...(style || {}) }}
        container={document.getElementById("main-app")}
        onClose={noCloseOnFocusOut ? undefined : e => {
          if (cancelAction) cancelAction(e);
        }}
        direction={styleDirection}
        {...otherProps}
      >
        {dialogTitle
          || (!dialogTitle && title && (
            <DialogTitle>
              <div css={diagTitleRootStyle}>
                <div className="title-div">{title}</div>
                <div className="top-right-buttons">
                  {topRightActions}
                  {xCancelAction && !noXicon && (
                    <IconButton size="small" color="inherit" data-role="cancel-btn" onClick={xCancelAction} className="top-left-x-icon" data-testid="dialog-close-x-btn">
                      <CloseIcon />
                    </IconButton>
                  )}
                </div>
              </div>
            </DialogTitle>
          ))}
        <DialogContent>{contentLoading}</DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog
      style={{ position: "absolute", ...(style || {}) }}
      container={document.getElementById("main-app")}
      onClose={noCloseOnFocusOut ? undefined : e => {
        if (xCancelAction) xCancelAction(e);
      }}
      {...otherProps}
    >
      {dialogTitle
        || (!dialogTitle && title && (
          <DialogTitle style={titleContainerStyle}>
            <div
              css={diagTitleRootStyle}
              data-testid="top_bar_dialog"
              style={{ direction: styleDirection }}
            >
              <div className="title-div">
                {title}
              </div>

              <div className="top-right-buttons">
                {topRightActions}
                {xCancelAction && !noXicon && (
                  <IconButton
                    color="inherit"
                    data-role="cancel-btn"
                    onClick={xCancelAction}
                    className="top-left-x-icon"
                    data-testid="dialog-close-x-btn"
                    style={closeBtnStyle}
                  >
                    <CloseIcon />
                  </IconButton>
                )}
              </div>
            </div>
          </DialogTitle>
        ))}

      {dialogContent || (content ? <DialogContent ref={ref} className="custom-scrollbar" css={dialogContentStyles} style={dialogContentStyle}>{content}</DialogContent> : null)}

      {(!hideActionRow && dialogActions) || (
        <DialogActions style={dialogActionStyle}>
          <div css={actionsCSS} style={{ direction: styleDirection }} className="MuiDialogActions-spacing">

            {!!leftSideAction && leftSideAction}
            {leftSideAction && <div className="space" />}
            {isLoadingActions && <CircularProgress size={15} thickness={3} color="primary" />}
            {!!customActions && customActions}
            {!noCancelButton && cancelAction && (
              <Button color="secondary" data-role="cancel-btn" onClick={cancelAction} disabled={isLoadingActions} data-testid="dialog-save-cancel">
                {cancelLabel || <Trans>Cancel</Trans>}
              </Button>
            )}
            {okAction && (
              <Tooltip
                disableFocusListener={!okDisabled || !okDisabledTooltip}
                disableHoverListener={!okDisabled || !okDisabledTooltip}
                title={<Trans render={translate => (translate.translation)}>{okDisabledTooltip || ""}</Trans>}
                placement="top"
                PopperProps={{
                  className: "MuiTooltip-popper MuiTooltip-popperInteractive pre-wrap-tooltip",
                  style: okDisabledTooltipZIndex ? { zIndex: okDisabledTooltipZIndex } : {},
                }}
              >
                <div>
                  <CommonButton
                    color={okColor || undefined}
                    style={{ opacity: "1 !important" }}
                    onClick={okAction}
                    disabled={okDisabled || isLoadingActions}
                    data-testid="dialog-save-btn"
                    data-cy="save-btn"
                  >
                    {okLabel || <Trans>Save</Trans>}
                  </CommonButton>
                </div>
              </Tooltip>
            )}
          </div>
        </DialogActions>
      )}
    </Dialog>
  );
});

CommonDialog.propTypes = {
  isrtl: t.bool,
  okAction: t.oneOfType([t.bool, t.func]),
  okLabel: t.oneOfType([t.string, t.object]),
  okColor: t.string,
  cancelAction: t.func,
  xAction: t.oneOfType([t.bool, t.func]),
  cancelLabel: t.oneOfType([t.string, t.object]),
  okDisabled: t.bool,
  okDisabledTooltip: t.oneOfType([t.string, t.object, t.element]),
  content: t.oneOfType([t.arrayOf(t.node), t.node, t.string]),
  children: t.oneOfType([t.arrayOf(t.node), t.node, t.string]),
  title: t.oneOfType([t.string, t.element]),
  noCancelButton: t.bool,
  isLoadingActions: t.bool,
  noCloseOnFocusOut: t.bool,
  contentLoading: t.oneOfType([t.arrayOf(t.node), t.node, t.string, t.element]),
  noXicon: t.bool,
  leftSideAction: t.oneOfType([t.arrayOf(t.node), t.node, t.string, t.element]),
  customActions: t.oneOfType([t.arrayOf(t.node), t.node, t.string]),
  topRightActions: t.oneOfType([t.arrayOf(t.element), t.element]),
  okDisabledTooltipZIndex: t.number,
  style: t.object,
};

export default CommonDialog;
